import LimitedEditionIcon from '@/assets/all-icons/limited-edition-icon.jsx';
import useTranslations from '@/i18n/useTranslations';
import { isLimitedEditionAvailable } from '@/pages/artwork/utils/artworkView.selectors';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import NumberUtils from '@/utils/number.utils';

import LimitedEditionSoldOutSummary from './components/LimitedEditionSoldOutSummary';

import './styles/LimitedEditionSummary.css';

function formatDimensions(entry) {
  if (!entry) {
    return '-';
  }
  const { width, height } = entry;
  if (width && height) {
    return `${width} cm x ${height} cm`;
  }
  return entry.size ?? entry.name ?? '-';
}

function formatPrice(value, currency) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    try {
      return NumberUtils.getArtworkPrice(value, currency);
    } catch (error) {
      console.warn('[LimitedEditionSummary] Unable to format price', error);
    }
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  return '-';
}

function hasAvailability(entry) {
  if (!entry) {
    return false;
  }
  return isLimitedEditionAvailable(entry);
}

export default function LimitedEditionSummary({
  editions = {},
  currency,
  onEditionSelect,
  selectedKey = null,
  hideList = false,
  onShowOwners = null,
}) {
  const t = useTranslations('common.shared.artwork_view.details.limited_edition_summary');
  if (!editions || typeof editions !== 'object') {
    return null;
  }

  const entries = Object.entries(editions)
    .map(([key, data]) => ({ key, data }))
    .filter((entry) => entry.data);

  if (!entries.length) {
    return null;
  }

  const canShowOwnersButton = typeof onShowOwners === 'function';

  const handleSelection = (key, data) => {
    if (typeof onEditionSelect !== 'function') {
      return;
    }
    onEditionSelect(key, data);
  };

  return (
    <div className="limited-edition-summary">
      <div className="limited-edition-summary__header">
        <div className="limited-edition-summary__title-block">
          <span className="limited-edition-summary__title">Limited editions</span>
          <span className="limited-edition-summary__badge" aria-hidden="true">
            <LimitedEditionIcon className="limited-edition-summary__badge-icon" />
          </span>
        </div>
        {canShowOwnersButton && (
          <ContinueButton
            className="limited-edition-summary__owners-button"
            onClick={onShowOwners}
            type="button"
          >
            Owners
          </ContinueButton>
        )}
      </div>
      {!hideList ? (
        <>
          <div className="limited-edition-summary__list">
            {entries.map(({ key, data }) => {
              const isActive = selectedKey === key;
              const isSoldOut = !hasAvailability(data);
              const itemDisabled = typeof onEditionSelect !== 'function' || isSoldOut;
              return (
                <button
                  type="button"
                  key={key}
                  className={[
                    'limited-edition-summary__item',
                    isActive ? 'limited-edition-summary__item--active' : '',
                    itemDisabled ? 'limited-edition-summary__item--disabled' : '',
                    isSoldOut ? 'limited-edition-summary__item--sold-out' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleSelection(key, data)}
                  aria-pressed={isActive}
                  aria-disabled={itemDisabled}
                  disabled={itemDisabled}
                >
                  <div className="limited-edition-summary__item-content">
                    <div className="limited-edition-summary__item-main">
                      <span className="limited-edition-summary__item-size">{formatDimensions(data)}</span>
                      <span className="limited-edition-summary__item-meta">
                        {t('edition_of', { count: data.quantity ?? '-' })}
                      </span>
                    </div>
                    <span className="limited-edition-summary__item-price">
                      {formatPrice(data.price, currency)}
                    </span>
                  </div>
                  {isSoldOut ? (
                    <span className="limited-edition-summary__sold-out-stamp">Sold out</span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <LimitedEditionSoldOutSummary editions={editions} />
        </>
      ) : null}
    </div>
  );
}
